import FolderTreeModal from '@anm/components/modals/FolderTreeModal';
import noop from '@anm/helpers/noop';
import useEventPending from '@anm/hooks/useEventPending';
import { useCallback, useEffect, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { videoFolderActions, videoFolderSelectors } from '../../../store/modules/videoFolder';
import { findFolder } from '../../../store/modules/videoFolder/helpers';

export type ProjectFolderTreeModalProps = {
  onMoveFolder: (folderContainerId: number) => void;
  parentId?: number;
  moveItemName?: string;
  customHeader?: string;
  rightButtonText?: string;
  excludeFolderId?: number;
  selectedFolderId?: number;
  onClose?: () => void;
};

const ProjectFolderTreeModal: FC<ProjectFolderTreeModalProps> = ({
  moveItemName,
  customHeader,
  onMoveFolder,
  onClose = noop,
  excludeFolderId,
  selectedFolderId,
  rightButtonText = 'Move'
}) => {
  const dispatch = useDispatch();
  const [canFocus, setCanFocus] = useState(false);
  const folderTreeWithoutExcluded = useSelector(videoFolderSelectors.selectFolderTreeWithout(excludeFolderId));

  const folderTree = useSelector(videoFolderSelectors.selectFolderTree);
  const videoFolder = useSelector(videoFolderSelectors.selectVideoFolders)!;
  const { newFolder, moveFolder, moveProject } = videoFolder!;
  const isPending = moveFolder.isPending || moveProject.isPending;
  const statEventPending = useEventPending(isPending, onClose);

  useEffect(() => () => onClose(), []);

  // open parent folders in loop
  useEffect(() => {
    if (!selectedFolderId) return;

    let folderIdToOpen: number | undefined = selectedFolderId;

    folderIdToOpen && dispatch(videoFolderActions.expandFolderInTree({ folderId: folderIdToOpen, justOpen: true }));

    do {
      const folder = findFolder(folderTree, folderIdToOpen!);

      if (!folder) return;

      folderIdToOpen = folder.parentId;
      folder.parentId && dispatch(videoFolderActions.expandFolderInTree({ folderId: folder.parentId, justOpen: true }));
    } while (folderIdToOpen);
  }, [selectedFolderId]);

  const handleExpandFolder = useCallback(
    (folderId: number) => dispatch(videoFolderActions.expandFolderInTree({ folderId })),
    []
  );
  const handleMoveFolder = useCallback(
    (parentId: number) => {
      onMoveFolder(parentId);
      statEventPending();
    },
    [onMoveFolder, statEventPending]
  );
  const handleCreateFolder = useCallback((parentId: number) => {
    setCanFocus(true);
    dispatch(videoFolderActions.createFolder({ parentId }));
    dispatch(
      videoFolderActions.expandFolderInTree({
        folderId: parentId,
        justOpen: true
      })
    );
  }, []);
  const handleRenameFolder = useCallback(({ id: folderId, newName, oldName }) => {
    const canUpdate = newName && oldName !== newName;
    setCanFocus(false);
    canUpdate && dispatch(videoFolderActions.renameFolder({ folderId, name: newName }));
  }, []);

  if (!folderTree) return null;

  const newFolderData = videoFolder.newFolder.data;
  const newFolderId = newFolderData && newFolderData.id;
  const isInputFocused = (folderId: number) => canFocus && newFolderId === folderId;

  return (
    <FolderTreeModal
      {...{ rightButtonText, newFolderId, selectedFolderId }}
      data={[excludeFolderId ? folderTreeWithoutExcluded : folderTree]}
      header={customHeader || `Move your ${moveItemName} to...`}
      isInputFocused={isInputFocused}
      isMoveFolderPending={isPending}
      isCreateFolderPending={newFolder.isPending}
      onMoveFolder={handleMoveFolder}
      onExpandFolder={handleExpandFolder}
      onCreateFolder={handleCreateFolder}
      onRenameFolder={handleRenameFolder}
      onModalClose={onClose}
    />
  );
};

export default ProjectFolderTreeModal;
